import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import {
  canonicalJson,
  GuideCanaryTraceSchema,
  GuideCanaryValidationReportSchema,
  parseContract,
} from "@rnavoyager/contracts";
import type {
  AdminGuideResponse,
  GuideCanaryTraceContract,
  GuideCanaryValidationReportContract,
} from "@rnavoyager/contracts";

export const GUIDE_REVIEW_MAX_FILE_BYTES = 8 * 1024 * 1024;
export interface GuideReviewEvidence {
  readonly trace: GuideCanaryTraceContract;
  readonly report: GuideCanaryValidationReportContract;
}

/** 与运行器使用同一规范JSON；纯JS摘要也可在现有HTTP内网部署中运行。 */
export function guideReviewDigest(value: unknown): string {
  return bytesToHex(sha256(new TextEncoder().encode(canonicalJson(value))));
}

export function bindGuideReviewEvidence(
  guide: AdminGuideResponse,
  traceValue: unknown,
  reportValue: unknown,
): GuideReviewEvidence {
  const canary = guide.canary;
  if (!canary) throw new Error("当前Guide没有已登记的验收证据");
  const trace = parseContract<GuideCanaryTraceContract>(GuideCanaryTraceSchema, traceValue);
  const report = parseContract<GuideCanaryValidationReportContract>(
    GuideCanaryValidationReportSchema,
    reportValue,
  );
  if (
    guideReviewDigest(trace) !== canary.traceDigest ||
    guideReviewDigest(report) !== canary.validatorDigest ||
    guideReviewDigest(trace.cleanup) !== canary.cleanupEvidenceDigest ||
    trace.definitionId !== guide.definition.definitionId ||
    trace.contentDigest !== guide.definition.contentDigest ||
    canary.contentDigest !== guide.definition.contentDigest ||
    report.definitionId !== trace.definitionId ||
    report.contentDigest !== trace.contentDigest ||
    report.traceDigest !== canary.traceDigest ||
    report.passed !== canary.passed ||
    trace.runtimeDigest !== canary.runtimeDigest ||
    trace.protocolDigest !== canary.protocolDigest ||
    trace.serverId !== canary.serverId ||
    trace.startedAt !== canary.executedAt ||
    trace.cleanup.completedAt !== canary.cleanupCompletedAt ||
    trace.authorizationEnvelopeDigest !== canary.authorizationEnvelopeDigest
  )
    throw new Error("所选文件与当前Guide已登记的验收摘要不匹配，请重新获取当前证据");
  if (
    report.passed &&
    (!report.budgetPassed || report.problems.length || report.assertions.some((a) => !a.passed))
  )
    throw new Error("验收报告的通过状态与检查结果矛盾");
  return { trace, report };
}

export async function readGuideReviewFile(file: File): Promise<unknown> {
  if (file.size < 1 || file.size > GUIDE_REVIEW_MAX_FILE_BYTES)
    throw new Error("验收文件必须为非空JSON且不超过8 MiB");
  const text = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      typeof reader.result === "string"
        ? resolve(reader.result)
        : reject(new Error("无法读取验收文件"));
    reader.onerror = () => reject(new Error("无法读取验收文件"));
    reader.readAsText(file);
  });
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error("验收文件不是有效JSON");
  }
}
